import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { User } from '@/types/domain.types';
import type { FundingPool } from '@/api/schemas/funding-pool.schema';
import { DEFAULT_CURRENCY } from '@/lib/constants';
import { logger } from '@/lib/logger';

interface PoolInsertFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pools: FundingPool[];
  groupMembers: User[];
  currentUserId: string;
  onSubmit: (data: {
    poolId?: string;
    amount: number;
    currency_code: string;
    inserted_by?: string;
    description?: string;
    date?: string;
  }) => Promise<void>;
}

export const PoolInsertForm: React.FC<PoolInsertFormProps> = ({
  open,
  onOpenChange,
  pools,
  groupMembers,
  currentUserId,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [poolRef, setPoolRef] = React.useState('shared_pool');
  const [amount, setAmount] = React.useState('');
  const [currency, setCurrency] = React.useState(DEFAULT_CURRENCY);
  const [insertedBy, setInsertedBy] = React.useState(currentUserId);
  const [description, setDescription] = React.useState('');
  const [date, setDate] = React.useState('');

  React.useEffect(() => {
    if (poolRef === 'shared_pool' || !poolRef) {
      return;
    }
    const selected = pools.find((pool) => pool.id === poolRef);
    if (selected?.currency_code) {
      setCurrency(selected.currency_code || DEFAULT_CURRENCY);
    }
  }, [poolRef, pools]);

  React.useEffect(() => {
    if (open) {
      setInsertedBy(currentUserId);
      setDate('');
    }
  }, [open, currentUserId]);

  const handlePoolChange = (value: string) => {
    setPoolRef(value);
    if (value === 'shared_pool') {
      setCurrency(DEFAULT_CURRENCY);
      return;
    }
    const selected = pools.find((pool) => pool.id === value);
    if (selected?.currency_code) {
      setCurrency(selected.currency_code || DEFAULT_CURRENCY);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      alert('Please enter a positive amount');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        poolId: poolRef === 'shared_pool' ? undefined : poolRef,
        amount: parsedAmount,
        currency_code: currency,
        inserted_by: insertedBy || undefined,
        description: description || undefined,
        date: date ? new Date(date).toISOString() : undefined,
      });
      setAmount('');
      setDescription('');
      setDate('');
      onOpenChange(false);
    } catch (error) {
      logger.error('Failed to insert into pool', { error });
      alert('Failed to insert into pool. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Insert Into Shared Pool</DialogTitle>
            <DialogDescription>
              Add money to a pool. The app will create automatic debt adjustments.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="pool">Pool</Label>
              <Select value={poolRef} onValueChange={handlePoolChange} disabled={isSubmitting}>
                <SelectTrigger id="pool">
                  <SelectValue placeholder="Select pool" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared_pool">Shared pool (auto create)</SelectItem>
                  {pools.map((pool) => (
                    <SelectItem key={pool.id} value={pool.id}>
                      {pool.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="inserted-by">Inserted by</Label>
              <Select value={insertedBy} onValueChange={setInsertedBy} disabled={isSubmitting}>
                <SelectTrigger id="inserted-by">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {groupMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name} {member.id === currentUserId ? '(You)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="amount">Amount</Label>
              <div className="flex gap-2">
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  disabled={isSubmitting}
                />
                <Input
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                  className="w-24"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="e.g., Added cash for groceries"
                rows={2}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="date">Date (optional)</Label>
              <Input
                id="date"
                type="datetime-local"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Insert'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
